import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const StudentIdIntegration = ({ studentData, verificationStatus, campusServices }) => {
  const [showQRCode, setShowQRCode] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const formatStudentId = (id) => {
    // Format student ID with dashes for readability
    return id?.replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3');
  };

  const getVerificationStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'var(--color-success)';
      case 'pending':
        return 'var(--color-warning)';
      case 'failed':
        return 'var(--color-error)';
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  const getVerificationStatusText = (status) => {
    switch (status) {
      case 'verified':
        return 'Verificado';
      case 'pending':
        return 'Pendiente';
      case 'failed':
        return 'Falló';
      default:
        return 'No verificado';
    }
  };

  const handleVerification = async () => {
    setIsVerifying(true);
    // Simulate verification process
    setTimeout(() => {
      setIsVerifying(false);
      // Handle verification result
    }, 2000);
  };

  const getServiceIcon = (serviceType) => {
    const icons = {
      'library': 'BookOpen',
      'cafeteria': 'Utensils',
      'gym': 'Dumbbell',
      'transport': 'Bus',
      'parking': 'Car',
      'printing': 'Printer'
    };
    return icons?.[serviceType] || 'Circle';
  };

  return (
    <div className="bg-card rounded-xl border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="CreditCard" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-lg text-foreground">
              Integración ID Estudiantil
            </h3>
            <p className="text-sm text-muted-foreground">
              Acceso unificado a servicios del campus
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowQRCode(!showQRCode)}
        >
          <Icon name="QrCode" size={16} className="mr-2" />
          {showQRCode ? 'Ocultar' : 'Mostrar'} QR
        </Button>
      </div>
      {/* Student ID Card */}
      <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl p-6 text-primary-foreground mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-16 h-16 bg-primary-foreground/20 rounded-full flex items-center justify-center">
              {studentData?.photo ? (
                <img 
                  src={studentData?.photo} 
                  alt={`Foto de perfil de ${studentData?.name}`}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <Icon name="User" size={32} color="var(--color-primary-foreground)" />
              )}
            </div>
            <div>
              <h4 className="font-heading font-bold text-lg">{studentData?.name}</h4>
              <p className="text-primary-foreground/80 text-sm">{studentData?.career}</p>
              <p className="text-primary-foreground/70 text-xs">
                ID: {formatStudentId(studentData?.studentId)}
              </p>
            </div>
          </div>
          
          <div className="text-right">
            <div className="flex items-center space-x-2 mb-2">
              <Icon 
                name={verificationStatus === 'verified' ? 'CheckCircle' : 'AlertCircle'} 
                size={16} 
                color={getVerificationStatusColor(verificationStatus)}
              />
              <span className="text-sm font-medium">
                {getVerificationStatusText(verificationStatus)}
              </span>
            </div>
            <p className="text-primary-foreground/70 text-xs">
              Válido hasta: {new Intl.DateTimeFormat('es-MX')?.format(studentData?.expiryDate)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-primary-foreground/70">Facultad</p>
            <p className="font-medium">{studentData?.faculty}</p>
          </div>
          <div>
            <p className="text-primary-foreground/70">Semestre</p>
            <p className="font-medium">{studentData?.semester}°</p>
          </div>
          <div>
            <p className="text-primary-foreground/70">Matrícula</p>
            <p className="font-medium">{studentData?.enrollmentYear}</p>
          </div>
          <div>
            <p className="text-primary-foreground/70">Estado</p>
            <p className="font-medium">{studentData?.status}</p>
          </div>
        </div>
      </div>
      {/* QR Code Display */}
      {showQRCode && (
        <div className="bg-muted/30 rounded-lg p-6 mb-6 text-center">
          <div className="w-48 h-48 bg-background rounded-lg mx-auto mb-4 flex items-center justify-center">
            <div className="text-center">
              <Icon name="QrCode" size={64} color="var(--color-muted-foreground)" className="mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Código QR del ID Estudiantil
              </p>
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Escanea este código para acceder a servicios del campus
          </p>
        </div>
      )}
      {/* Verification Status */}
      {verificationStatus !== 'verified' && (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <Icon name="AlertTriangle" size={20} color="var(--color-warning)" />
            <div className="flex-1">
              <h4 className="font-medium text-foreground">Verificación Requerida</h4>
              <p className="text-sm text-muted-foreground">
                {verificationStatus === 'pending' ?'Tu verificación está siendo procesada. Esto puede tomar hasta 24 horas.' :'Necesitas verificar tu ID estudiantil para acceder a todos los servicios.'
                }
              </p>
            </div>
            {verificationStatus !== 'pending' && (
              <Button
                variant="warning"
                size="sm"
                onClick={handleVerification}
                loading={isVerifying}
              >
                {isVerifying ? 'Verificando...' : 'Verificar Ahora'}
              </Button>
            )}
          </div>
        </div>
      )}
      {/* Campus Services */}
      <div>
        <h4 className="font-medium text-foreground mb-4">Servicios del Campus Disponibles</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {campusServices?.map((service) => (
            <div
              key={service?.id}
              className={`flex items-center justify-between p-3 rounded-lg border transition-smooth ${
                service?.available && verificationStatus === 'verified'
                  ? 'border-success/20 bg-success/5' :'border-border bg-muted/30'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  service?.available && verificationStatus === 'verified'
                    ? 'bg-success/10' :'bg-muted'
                }`}>
                  <Icon 
                    name={getServiceIcon(service?.type)} 
                    size={16} 
                    color={service?.available && verificationStatus === 'verified' 
                      ? 'var(--color-success)' :'var(--color-muted-foreground)'
                    }
                  />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{service?.name}</p>
                  <p className="text-xs text-muted-foreground">{service?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                {service?.available && verificationStatus === 'verified' ? (
                  <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                ) : (
                  <Icon name="Lock" size={16} color="var(--color-muted-foreground)" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Integration Benefits */}
      <div className="mt-6 bg-gradient-to-r from-accent/5 to-primary/5 rounded-lg p-4">
        <h4 className="font-medium text-foreground mb-3 flex items-center space-x-2">
          <Icon name="Star" size={16} color="var(--color-accent)" />
          <span>Beneficios de la Integración</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="flex items-center space-x-2">
            <Icon name="Zap" size={14} color="var(--color-accent)" />
            <span className="text-sm text-foreground">Acceso rápido sin contraseñas</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Shield" size={14} color="var(--color-success)" />
            <span className="text-sm text-foreground">Verificación automática de estudiante</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="CreditCard" size={14} color="var(--color-primary)" />
            <span className="text-sm text-foreground">Pagos unificados en campus</span>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Bell" size={14} color="var(--color-warning)" />
            <span className="text-sm text-foreground">Notificaciones personalizadas</span>
          </div>
        </div>
      </div>
      {/* Action Buttons */}
      <div className="flex items-center space-x-2 mt-6">
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="Settings" size={16} className="mr-2" />
          Configurar Servicios
        </Button>
        <Button variant="outline" size="sm" className="flex-1">
          <Icon name="HelpCircle" size={16} className="mr-2" />
          Ayuda y Soporte
        </Button>
      </div>
    </div>
  );
};

export default StudentIdIntegration;